<template>
  <router-view />
</template>

<script>
import PageMixin from '@/mixins/PageMixin'
import externalLinks from '@/lib/external-links'
import 'flickity-bg-lazyload'
import { getCart } from '@/lib/shopify-utils'

export default {
  name: 'app',
  mixins: [PageMixin],
  async created() {
    externalLinks()
    this.fetchData('/*')
    this.fetchData()
    window.addEventListener('resize', () => {
      this.$store.commit('updateMobile')
    })

    if (window.location.hash === '#channels') {
      this.$store.dispatch('setShowChannels', true)
      document.body.style.overflow = 'hidden'
    }

    const cartData = await getCart()

    this.$store.dispatch('setCartData', cartData)
  },
  methods: {
    fetchData(pathname) {
      pathname = typeof pathname === 'string' ? pathname : this.$route.path
      if (!this.$store.state.content[pathname]) {
        this.$store.dispatch('fetchData', pathname)
      }
    },
    closeNavigation() {
      this.$store.commit('updateNavigationMeta', { open: false })
    },
    prerenderReady() {
      // pre-render ready for Netlify
      const pathname = this.$route.path
      if (this.$store.state.content[pathname] && !window.prerenderReady) {
        window.prerenderReady = true
      }
    },
  },
  watch: {
    $route: ['fetchData', 'closeNavigation'],
    hasPage: 'prerenderReady',
  },
  metaInfo() {
    // Site Meta
    let meta = Object.assign(
      {},
      {
        title: this.site.title,
        description: this.site.description,
        image: this.site.featuredimage ? this.site.featuredimage.original : '',
      }
    )

    // Page Meta
    if (this.page) {
      let page = this.page
      let pagemeta = {}

      if (page.title) pagemeta.title = page.title
      if (page.description) pagemeta.description = page.description
      if (page.featuredimage && (page.featuredimage.original || page.featuredimage.large)) {
        pagemeta.image = page.featuredimage.original || page.featuredimage.large
      }

      meta = Object.assign(meta, pagemeta)
    }

    let metaTags = [
      {
        name: 'facebook-domain-verification',
        content: '62r7fjryidvk7dkq882482dnixhnpz',
        vmid: 'facebook-domain-verification',
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
        vmid: 'og:card',
      },
      {
        name: 'twitter:image',
        content: meta.image,
        vmid: 'og:image',
      },
      {
        name: 'description',
        content: meta.description,
        vmid: 'description',
      },
      {
        property: 'og:title',
        content: meta.title,
        vmid: 'og:title',
      },
      {
        property: 'og:description',
        content: meta.description,
        vmid: 'og:description',
      },
      {
        property: 'og:type',
        content: 'website',
        vmid: 'og:type',
      },
      {
        property: 'og:image',
        content: meta.image,
        vmid: 'og:image',
      },
    ]

    if (this.isNotFound) {
      metaTags.push({
        property: 'prerender-status-code',
        content: 404,
        vmid: 'prerender-status-code',
      })
    }

    return {
      title: this.page ? this.page.title : this.site.title,
      meta: metaTags,
    }
  },
}
</script>

<style>
:root {
  --br: 0.75rem;
  --nord: normal 1.55rem/1.25 GintoNord-Medium, Arial, Helvetica, sans-serif;
  --nord-title: normal 3rem/1.25 GintoNord-Medium, Arial, Helvetica, sans-serif;
  --copy: normal 2.5rem/1.25 GintoNormal-Regular, Arial, Helvetica, sans-serif;
  --serif: normal 2.1rem/1.45 SuisseWorks-Regular, Times New Roman, Times, serif;
  --serif-small: normal 1.7rem/1.45 SuisseWorks-Regular, Times New Roman, Times, serif;
  --p1: 1.4rem 2rem 1.5rem 2rem;
}

*,
*:before,
*:after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-size: 62.5%;
}

@media (max-width: 1000px) {
  html {
    font-size: 58%;
  }
}

@media (max-width: 400px) {
  html {
    font-size: 50%;
  }
}

@media (min-width: 1750px) {
  html {
    font-size: 70%;
  }
}

body {
  font: var(--serif);
  overflow-x: hidden;
}

.wcag-hidden {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* UI Reset */

b,
strong {
  font-weight: normal;
}

h1,
h2,
h3,
h4,
h5,
h6,
h7 {
  font-size: inherit;
  font-weight: inherit;
  font-style: inherit;
}

button,
input {
  outline: none;
  letter-spacing: inherit;
  text-align: inherit;
}

ul,
ol,
li {
  list-style: none;
}

a {
  color: inherit;
  text-decoration: inherit;
}

img,
video {
  width: 100%;
  height: auto;
}

/**
 * only show focus rings 
 * when navigating with keyboard
 */

.js-focus-visible :focus:not(.focus-visible) {
  outline: none;
}

@font-face {
  font-family: GintoNord-Medium;
  src: url('assets/GintoNord-Medium.woff2') format('woff2'), url('assets/GintoNord-Medium.woff') format('woff');
}

@font-face {
  font-family: GintoNormal-Regular;
  src: url('assets/GintoNormal-Regular.woff2') format('woff2'), url('assets/GintoNormal-Regular.woff') format('woff');
}

/*@font-face {
  font-family: SuisseWorks-Medium;
  src: url('assets/SuisseWorks-Medium.woff2') format("woff2"), url('assets/SuisseWorks-Medium.woff') format("woff");
}*/

@font-face {
  font-family: SuisseWorks-Regular;
  src: url('assets/SuisseWorks.woff2') format('woff2'), url('assets/SuisseWorks.woff') format('woff');
}

/**
 * Type
 */

a {
  color: inherit;
}

h1,
h2,
h3,
h4,
.nord {
  font: var(--nord);
}

.links a {
  border-bottom: 1px solid currentColor;
}

.links-dashed a {
  border-bottom: 1px dashed currentColor;
}

@media (hover: hover) {
  .links a:hover,
  .links-dashed a:hover {
    border-bottom: none;
  }
}

.uppercase {
  text-transform: uppercase;
}

/**
 * Misc
 */

hr {
  border: none;
  color: inherit;
  border-bottom: 1px dashed currentColor;
  margin: 0 2rem;
}

.list > * {
  display: inline;
}
.list > *:not(:last-child):after {
  content: ', ';
}

.pointer {
  cursor: pointer;
}

.op0 {
  opacity: 0;
}

.noisy-image:not(:empty),
.noisy-image-invert:not(:empty) {
  background: url(assets/noise-500.gif);
  background-size: 125px;
  background-position: center;
  background-repeat: repeat;
}

.noisy-image > * {
  mix-blend-mode: hard-light;
  filter: grayscale(100%) contrast(150%) brightness(1.25);
}

.has-trailer .noisy-image > * {
  filter: contrast(110%) brightness(1.05);
}

.noisy-image-invert > * {
  mix-blend-mode: hard-light;
  filter: invert(100%) grayscale(100%) contrast(130%) brightness(1.03);
}

.color-red {
  color: #fa3b41;
}

.text-center {
  text-align: center;
}

.px-2 {
  padding-left: 2rem;
  padding-right: 2rem;
}

.mini-type {
  font: var(--nord);
  font-size: 1.15rem;
}

/**
 * Details/Summary
 */

details.dropdown {
  border: 1px dashed currentColor;
}

details.dropdown summary {
  border: none;
  font: var(--nord);
  display: flex;
  justify-content: space-between;
  line-height: 1;
  list-style-image: none;
  cursor: pointer;
  background-image: url(assets/dropdown.svg);
  background-position: right 1.5rem center;
  background-repeat: no-repeat;
  background-size: 1.7rem;
}

details.dropdown summary::-webkit-details-marker {
  display: none;
}

details.dropdown > * {
  user-select: none;
  padding: var(--p1);
}

/**
 * UI
 */

.mp-button,
.mp-input {
  display: block;
  border: 1px dashed currentColor;
  background: none;
  font: var(--nord);
  text-transform: uppercase;
  color: #fff;
  padding: 1.5rem 2rem;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  text-align: center;
  border-radius: 0;
}

.mp-input {
  text-align: left;
  cursor: text;
}

.mp-button[disabled='disabled'] {
  cursor: default;
  opacity: 0.25;
  pointer-events: none;
}

.mp-mini-button {
  border: none;
  background: none;
  font: var(--nord);
  color: #fff;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-size: 1.15rem;
  text-align: center;
}

.mp-mini-button.with-underline {
  text-decoration: underline;
}

@media (hover: hover) {
  .mp-mini-button.with-underline:hover {
    text-decoration: none;
  }
}

.mp-select {
  border: 1px dashed currentColor;
  background: none;
  font: var(--nord);
  text-transform: uppercase;
  color: #fff;
  padding: 1.5rem 5rem 1.5rem 2rem;
  background-image: url(assets/dropdown.svg);
  background-position: right 1.5rem center;
  background-repeat: no-repeat;
  background-size: 1.7rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}

.mp-number {
  /*display: inline-block;
  white-space: nowrap;*/
}

.mp-number input[type='number']::-webkit-inner-spin-button,
.mp-number input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.mp-number input[type='number'] {
  -moz-appearance: textfield;
  width: 2em;
  border: none;
  background: none;
  font: var(--nord);
  color: #fff;
}

.mp-number button {
  width: 1.5em;
  height: 1.5em;
  display: inline-block;
  border: 1px solid #333;
  background: none;
  font: var(--nord);
  color: #fff;
  text-align: center;
  cursor: pointer;
  margin-left: -1px;
}

.mp-number [disabled='disabled'] {
  cursor: default;
  opacity: 0.5;
  pointer-events: none;
}

.mp-chk {
  position: absolute;
  clip: rect(0, 0, 0, 0);
}
.mp-chk + label {
  user-select: none;
  position: relative;
}
.mp-chk + label:before {
  content: '';
  display: inline-block;
  border: 1px solid white;
  width: 0.8em;
  height: 0.8em;
  position: relative;
  /*left: -1.35em;
  top: 0.38em;*/
  top: 0.01em;
  margin-right: 0.8em;
}
.mp-chk:checked + label:before {
  content: '';
  display: inline-block;
  border: 1px solid white;
  background-color: white;
}
.mp-chk + label,
.mp-chk + label:before {
  cursor: pointer;
}

/*@media (hover:hover) {
  .mp-button:hover {
    background-color: #fff;
    color: #000;
  }
}*/

/**
 * Misc
 */

.loading {
  text-align: center;
  font: var(--nord);
  text-transform: uppercase;
}

figure.cover {
  overflow: hidden;
  border-radius: var(--br);
  position: relative;
  border: 1px dashed transparent;
}

figure.cover:empty {
  border-color: #fff;
}

figure.cover img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* .has-trailer-sticker {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #5a5a5a;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.has-trailer-sticker img {
  width: 40%;
  transform: translateX(11%);
} */

@media print {
  .hide-print {
    display: none !important;
  }

  .container {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}

.impact-module + .impact-module {
  margin-top: 6rem;
}

.impact-module .border {
  padding: 0 2rem 5rem 2rem;
}

.impact-module .border:before {
  content: '';
  display: block;
  width: 100%;
  border-top: 1px dashed white;
}
</style>
